import React, { useState } from 'react';
import { AllergenInfo, AllergenType } from '../types';
import './AllergenManager.css';

const ALLERGEN_OPTIONS: AllergenType[] = [
    'celery',
    'gluten',
    'crustaceans',
    'eggs',
    'fish',
    'lupin',
    'milk',
    'molluscs',
    'mustard',
    'nuts',
    'peanuts',
    'sesame',
    'soya',
    'sulphites'
];

const ALLERGEN_DISPLAY_NAMES: Record<AllergenType, string> = {
    celery: 'Celery',
    gluten: 'Gluten (Cereals)',
    crustaceans: 'Crustaceans',
    eggs: 'Eggs',
    fish: 'Fish',
    lupin: 'Lupin',
    milk: 'Milk',
    molluscs: 'Molluscs',
    mustard: 'Mustard',
    nuts: 'Tree Nuts',
    peanuts: 'Peanuts',
    sesame: 'Sesame',
    soya: 'Soya',
    sulphites: 'Sulphites'
};

interface AllergenManagerProps {
    allergens: AllergenInfo[];
    onChange: (allergens: AllergenInfo[]) => void;
    disabled?: boolean;
}

export const AllergenManager: React.FC<AllergenManagerProps> = ({
    allergens,
    onChange,
    disabled = false
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [newAllergen, setNewAllergen] = useState<AllergenType>('gluten');
    const [newSeverity, setNewSeverity] = useState<AllergenInfo['severity']>('contains');

    const handleAddAllergen = () => {
        console.log('handleAddAllergen called', { newAllergen, newSeverity, currentAllergens: allergens });

        // Check if allergen already exists
        if (allergens.some(a => a.allergen === newAllergen)) {
            alert('This allergen is already added.');
            return;
        }

        const allergenInfo: AllergenInfo = {
            allergen: newAllergen,
            severity: newSeverity
        };

        const updatedAllergens = [...allergens, allergenInfo];
        console.log('Calling onChange with:', updatedAllergens);
        onChange(updatedAllergens);

        // Reset form
        setNewAllergen('gluten');
        setNewSeverity('contains');
        setShowAddModal(false);
    };

    const handleRemoveAllergen = (allergen: AllergenType) => {
        onChange(allergens.filter(a => a.allergen !== allergen));
    };

    const getSeverityColor = (severity: AllergenInfo['severity']) => {
        switch (severity) {
            case 'contains': return '#e74c3c';
            case 'may_contain': return '#f39c12';
            case 'traces': return '#3498db';
        }
    };

    const getSeverityIcon = (severity: AllergenInfo['severity']) => {
        switch (severity) {
            case 'contains': return '⚠️';
            case 'may_contain': return '⚠️';
            case 'traces': return 'ℹ️';
        }
    };

    // Single inline experience for all cases
    return (
        <div className="allergen-manager">
            <button
                type="button"
                className="btn-add-allergen"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAddModal(true);
                }}
                disabled={disabled}
            >
                + Add Allergen
            </button>

            <div className="allergen-inline-list">
                {allergens.map((allergen) => (
                    <span
                        key={allergen.allergen}
                        className="allergen-badge"
                        style={{ borderColor: getSeverityColor(allergen.severity) }}
                        title={`${allergen.severity.replace('_', ' ')}`}
                    >
                        {getSeverityIcon(allergen.severity)} {ALLERGEN_DISPLAY_NAMES[allergen.allergen]}
                        <button
                            type="button"
                            className="allergen-remove-btn"
                            onClick={() => handleRemoveAllergen(allergen.allergen)}
                            disabled={disabled}
                            title="Remove allergen"
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>


            {showAddModal && (
                <div
                    className="allergen-modal-overlay"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAddModal(false);
                    }}
                >
                    <div
                        className="allergen-modal"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <h4>Add Allergen</h4>
                        <div className="allergen-form">
                            <label>
                                <span>Allergen Type</span>
                                <select
                                    value={newAllergen}
                                    onChange={(e) => setNewAllergen(e.target.value as AllergenType)}
                                >
                                    {ALLERGEN_OPTIONS.map(option => (
                                        <option key={option} value={option}>
                                            {ALLERGEN_DISPLAY_NAMES[option]}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Severity</span>
                                <select
                                    value={newSeverity}
                                    onChange={(e) => setNewSeverity(e.target.value as AllergenInfo['severity'])}
                                >
                                    <option value="contains">Contains</option>
                                    <option value="may_contain">May contain</option>
                                    <option value="traces">Traces</option>
                                </select>
                            </label>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAddAllergen();
                                    }}
                                    className="btn-primary"
                                >
                                    Add
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAddModal(false);
                                    }}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
